<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Button, Radio, Text } from '@Shelly/shelly-design-system/index';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import { app, os } from 'src/stores/session/native-bridge';
	import { hideBluetoothSettingsToast } from 'src/stores/ui/calibration-modals';
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import {
		control_bluetooth_device_ios,
		control_bluetooth_general_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		control_bluetooth_device_android,
		control_bluetooth_general_android,
	} from 'src/services/native_container_requests_android';
	import { calculateDistValues } from 'src/helpers/distance_helper';
	import { real_id } from 'src/helpers/interface_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { bindSettingsDrilldownBack, bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import {
		isBleSettingsSessionReady,
		persistBleDeviceSetting,
		readStoredBleDevice,
		resumeBleSettingsConnectionIfNeeded,
	} from 'src/helpers/ble_settings_session';
	import DynamicAlert from './DynamicAlert.svelte';

	$: settingsStore = getDeviceSettings(device.id);
	export let device;

	const storedDistanceRange = readStoredBleDevice(device)?.distanceRange;
	let distanceRange = storedDistanceRange ? `${storedDistanceRange}` : '7';
	let hadSession = false;

	$: sessionReady = isBleSettingsSessionReady($controlBleDevice.connectionState);
	$: if (sessionReady) hadSession = true;
	$: resumeBleSettingsConnectionIfNeeded(device.id, $controlBleDevice);
	$: controlsDisabled = !sessionReady || !$app;

	const goBackToSettingsList = bindSettingsDrilldownBack();

	const setDistanceRange = () => {
		hideBluetoothSettingsToast.set(false);
		if ($controlBleDevice.connectionState === 2) {
			$os === 'IOS'
				? control_bluetooth_general_ios(
						device.id,
						'setDistanceRange',
						'distanceRange',
						distanceRange,
					)
				: control_bluetooth_general_android(
						device.id,
						'setDistanceRange',
						'distanceRange',
						distanceRange,
					);

			persistBleDeviceSetting(device, { distanceRange });
			goBackToSettingsList?.();
		} else {
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'connect')
				: control_bluetooth_device_android(device.id, 'connect');
		}
	};

	$: distanceUnit = device?.ht_ui?.unit || $settingsStore?.ht_ui?.unit || 'mm';

	$: closeStart = calculateDistValues(200, distanceUnit);
	$: closeEnd = calculateDistValues(450, distanceUnit, true);
	$: midStart = calculateDistValues(300, distanceUnit);
	$: midEnd = calculateDistValues(3500, distanceUnit, true);
	$: farStart = calculateDistValues(2500, distanceUnit);
	$: farEnd = calculateDistValues(5000, distanceUnit, true);

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: setDistanceRange,
				loading: false,
				disabled: controlsDisabled,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}
</script>

<DynamicAlert showExpiredMsg={hadSession} />

<Text styleType="body-small" class="mb-s">
	{$format('text_set_distance_range', {
		default: 'Set the distance range that the device measures.',
	})}
</Text>

<Radio
	bind:selected={distanceRange}
	value="1"
	size="m"
	info={`${closeStart} - ${closeEnd}`}
	class="mb-s"
	disabled={controlsDisabled}
	id="radio-lnx2y4o"
>
	{$format('distance_range_option_close', { default: 'Close' })}
</Radio>
<Radio
	bind:selected={distanceRange}
	value="2"
	size="m"
	info={`${midStart} - ${midEnd}`}
	class="mb-s"
	disabled={controlsDisabled}
	id="radio-crmbuvn"
>
	{$format('distance_range_option_mid', { default: 'Mid' })}
</Radio>
<Radio
	bind:selected={distanceRange}
	value="3"
	size="m"
	info={`${closeStart} - ${midEnd}`}
	class="mb-s"
	disabled={controlsDisabled}
	id="radio-sco8ydh"
>
	{$format('distance_range_option_close_mid', { default: 'Close and Mid' })}
</Radio>
<Radio
	bind:selected={distanceRange}
	value="4"
	size="m"
	info={`${farStart} - ${farEnd}`}
	class="mb-s"
	disabled={controlsDisabled}
	id="radio-l22lwg9"
>
	{$format('distance_range_option_far', { default: 'Far' })}
</Radio>
<Radio
	bind:selected={distanceRange}
	value="5"
	size="m"
	info={`${closeStart} - ${closeEnd} ${$format('and', { default: 'and' })} ${farStart} - ${farEnd}`}
	class="mb-s"
	disabled={controlsDisabled}
	id="radio-ha5jnf3"
>
	{$format('distance_range_option_close_far', {
		default: 'Close and Far',
	})}
</Radio>
<Radio
	bind:selected={distanceRange}
	value="6"
	size="m"
	info={`${midStart} - ${farEnd}`}
	class="mb-s"
	disabled={controlsDisabled}
	id="radio-zn8qrln"
>
	{$format('distance_range_option_mid_far', { default: 'Mid and Far' })}
</Radio>
<Radio
	bind:selected={distanceRange}
	value="7"
	size="m"
	info={`${closeStart} - ${farEnd}`}
	class="mb-m"
	disabled={controlsDisabled}
	id="radio-unlefg4"
>
	{$format('distance_range_option_full', { default: 'Full' })}
</Radio>

{#if !$showNewLayout}
	<Button disabled={controlsDisabled} size="m" width="full" on:click={setDistanceRange}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
